import { useMutation, useQuery, useQueryClient } from "@tanstack/react-query";
import { createFileRoute } from "@tanstack/react-router";
import { useState } from "react";
import { z } from "zod";
import { Banner, Button, Card, EmptyState, Field, Input } from "@magarac/ui";
import { closureListSchema, closureSchema } from "@magarac/shared/schemas/resource";
import { HelpIcon } from "../../../components/HelpIcon";
import { api } from "../../../lib/api";
import { messageFor } from "../../../lib/errors";
import { useTenant } from "../../../lib/tenant";

export const Route = createFileRoute("/_app/settings/closures")({
  component: ClosuresPage,
});

/** SCH-13 business closures. Screen key: settings.closures */
function ClosuresPage() {
  const { active } = useTenant();
  const queryClient = useQueryClient();
  const list = useQuery({
    queryKey: ["closures", active?.id],
    queryFn: () => api.get("/closures", closureListSchema),
    enabled: Boolean(active),
  });
  const [from, setFrom] = useState("");
  const [to, setTo] = useState("");
  const [reason, setReason] = useState("");
  const [error, setError] = useState<string | null>(null);
  const refresh = () => queryClient.invalidateQueries({ queryKey: ["closures", active?.id] });

  const create = useMutation({
    mutationFn: () =>
      api.post(
        "/closures",
        {
          startAt: new Date(`${from}T00:00:00`).toISOString(),
          endAt: new Date(`${to || from}T23:59:59`).toISOString(),
          reason: reason || undefined,
        },
        z.object({ closure: closureSchema }),
      ),
    onSuccess: async () => {
      setFrom("");
      setTo("");
      setReason("");
      setError(null);
      await refresh();
    },
    onError: (err) => setError(messageFor(err)),
  });
  const remove = useMutation({
    mutationFn: (id: string) => api.delete(`/closures/${id}`, z.object({ ok: z.literal(true) })),
    onSuccess: refresh,
    onError: (err) => setError(messageFor(err)),
  });

  return (
    <div>
      <div className="mb-6 flex items-center justify-between">
        <h1 className="text-2xl font-semibold">Closures and holidays</h1>
        <HelpIcon screen="settings.closures" />
      </div>
      {error ? <Banner tone="danger">{error}</Banner> : null}
      <Card title="Add a closure" className="mb-6">
        <div className="grid gap-2 md:grid-cols-[1fr_1fr_2fr_auto] md:items-end">
          <Field id="from" label="First day">
            <Input id="from" type="date" value={from} onChange={(e) => setFrom(e.target.value)} />
          </Field>
          <Field id="to" label="Last day" hint="Leave empty for one day.">
            <Input id="to" type="date" value={to} onChange={(e) => setTo(e.target.value)} />
          </Field>
          <Field id="reason" label="Reason (optional)">
            <Input
              id="reason"
              value={reason}
              onChange={(e) => setReason(e.target.value)}
              placeholder="Thanksgiving"
            />
          </Field>
          <div className="mb-4">
            <Button onClick={() => create.mutate()} busy={create.isPending} disabled={!from}>
              Add
            </Button>
          </div>
        </div>
      </Card>
      <Card title="Upcoming closures">
        {list.data && list.data.closures.length === 0 ? (
          <EmptyState
            title="No closures yet"
            body="Add holidays or days the whole business is closed."
          />
        ) : (
          <ul>
            {list.data?.closures.map((c) => (
              <li
                key={c.id}
                className="flex items-center justify-between border-b border-line py-2 last:border-0"
              >
                <span>
                  {new Date(c.startAt).toLocaleDateString()} to{" "}
                  {new Date(c.endAt).toLocaleDateString()}
                  {c.reason ? <span className="text-muted"> · {c.reason}</span> : null}
                </span>
                <Button variant="ghost" onClick={() => remove.mutate(c.id)}>
                  Remove
                </Button>
              </li>
            ))}
          </ul>
        )}
      </Card>
    </div>
  );
}
